import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { END_APPOINTMENT_REASONS } from '../../constants/endAppointmentReasons';
import { normalizeToIsoDate } from '../../utils/buildDirectorFormData';

const EndAppointmentModal = ({
  open,
  onClose,
  onSubmit,
  title = 'End company appointment',
  description,
  companyName,
  showDeactivateLogin = false,
  defaultDeactivateLogin = true,
}) => {
  const [reason, setReason] = useState('');
  const [endDate, setEndDate] = useState(new Date());
  const [notes, setNotes] = useState('');
  const [deactivateLogin, setDeactivateLogin] = useState(defaultDeactivateLogin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (loading) return;
    setError('');
    setReason('');
    setNotes('');
    setEndDate(new Date());
    setDeactivateLogin(defaultDeactivateLogin);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason) {
      setError('Please select a reason');
      return;
    }
    const isoDate = normalizeToIsoDate(endDate);
    if (!isoDate) {
      setError('Please select an end date');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await onSubmit({
        reason,
        end_date: isoDate,
        notes: notes.trim() || undefined,
        deactivate_login: showDeactivateLogin ? deactivateLogin : undefined,
      });
      handleClose();
    } catch (err) {
      const message = err.response?.data?.error?.message || err.message || 'Failed to end appointment';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ overflow: 'visible' }}>
        {description && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        {companyName && (
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Company: {companyName}
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel id="end-appointment-reason-label">Reason</InputLabel>
            <Select
              labelId="end-appointment-reason-label"
              value={reason}
              label="Reason"
              onChange={(e) => setReason(e.target.value)}
            >
              {END_APPOINTMENT_REASONS.map((opt) => (
                <MenuItem key={opt.code} value={opt.code}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label="End date"
            value={endDate}
            onChange={(value) => setEndDate(value)}
            inputFormat="dd/MM/yyyy"
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
              },
            }}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional context for the audit log"
          />

          {showDeactivateLogin && (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={(
                <Checkbox
                  checked={deactivateLogin}
                  onChange={(e) => setDeactivateLogin(e.target.checked)}
                />
              )}
              label="Also mark inactive across all companies"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          End appointment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EndAppointmentModal;
